/**
 * KOPS-468: Team Management
 * aisearchportal.com/admin/team
 * Navy + Gold split-panel layout (matches ContactsPage pattern)
 */

import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faUsers, faUserPlus, faEnvelope, faPhone,
  faShieldAlt, faKey, faPowerOff, faCheckCircle,
  faClock, faUserCog, faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

interface Employee {
  id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_phone: string | null;
  user_role: string | null;
  subscription_package_name: string | null;
  last_login: string | null;
  created_on: string;
  connected_emails: number;
}

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db'
};

const TeamPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Right panel
  const [selected, setSelected] = useState<Employee | null>(null);

  // Actions
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Add employee form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '' });
  const [adding, setAdding] = useState(false);

  // Reset password
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [editingTeamMember, setEditingTeamMember] = useState(false);
  const [editTeamForm, setEditTeamForm] = useState({ user_first_name: '', user_last_name: '', user_email: '', user_phone: '' });

  useEffect(() => { fetchTeam(); }, []);

  const fetchTeam = async () => {
    try {
      const response = await axiosInstance.get('/admin/team');
      if (response.data.success) {
        setEmployees(response.data.employees);
      }
    } catch (err) {
      console.error('Failed to fetch team:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.email || !newEmployee.password || !newEmployee.firstName || !newEmployee.lastName) {
      setAlert({ type: 'danger', message: 'All fields except phone are required' });
      return;
    }
    if (newEmployee.password.length < 8) {
      setAlert({ type: 'danger', message: 'Password must be at least 8 characters' });
      return;
    }
    setAdding(true);
    try {
      const response = await axiosInstance.post('/admin/team', newEmployee);
      setAlert({ type: 'success', message: response.data.message });
      setShowAddForm(false);
      setNewEmployee({ email: '', password: '', firstName: '', lastName: '', phone: '' });
      fetchTeam();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to add employee' });
    } finally {
      setAdding(false);
    }
  };

  const handleDeactivate = async (emp: Employee) => {
    if (!window.confirm(`Deactivate ${emp.user_first_name} ${emp.user_last_name}? They will lose InternalEmployee access.`)) return;
    setActionLoading(true);
    try {
      await axiosInstance.put(`/admin/team/${emp.id}/deactivate`);
      setAlert({ type: 'success', message: `${emp.user_first_name} deactivated` });
      setSelected(null);
      fetchTeam();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to deactivate' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (emp: Employee) => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`/admin/team/${emp.id}/convert`);
      setAlert({ type: 'success', message: `${emp.user_first_name} reactivated as InternalEmployee` });
      fetchTeam();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to reactivate' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selected || !newPassword || newPassword.length < 8) {
      setAlert({ type: 'danger', message: 'Password must be at least 8 characters' });
      return;
    }
    setActionLoading(true);
    try {
      await axiosInstance.post(`/admin/team/${selected.id}/reset-password`, { newPassword });
      setAlert({ type: 'success', message: `Password reset for ${selected.user_first_name}` });
      setShowResetPassword(false);
      setNewPassword('');
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to reset password' });
    } finally {
      setActionLoading(false);
    }
  };

  const isInternalEmployee = (emp: Employee) =>
    emp.user_role === 'InternalEmployee' || emp.subscription_package_name === 'InternalEmployee';

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const filteredEmployees = employees.filter(emp => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (emp.user_first_name + ' ' + emp.user_last_name).toLowerCase().includes(q)
      || emp.user_email.toLowerCase().includes(q);
  });

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 0' }}>
        <Container fluid className="px-4 d-flex justify-content-between align-items-center">
          <div>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal
              </span>
            </a>
            <span style={{ color: colors.muted, fontSize: '14px', marginLeft: '16px' }}>/ Team</span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a href="/admin/subscribers" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Subscribers</a>
            <a href="/admin/team" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '13px', padding: '6px 12px', fontWeight: 600 }}>Team</a>
            <a href="/admin/contacts" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Contacts</a>
            <a href="/admin/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Events</a>
            <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Dashboard</a>
          </div>
        </Container>
      </nav>

      {alert && (
        <div style={{ padding: '0 24px', paddingTop: '12px' }}>
          <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>
        </div>
      )}

      {/* Stats Strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '12px 24px' }}>
        <div className="d-flex gap-4 align-items-center">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faUsers} style={{ color: colors.blue, fontSize: '13px' }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '16px' }}>{employees.length}</span>
            <span style={{ color: colors.muted, fontSize: '11px' }}>Team Members</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faShieldAlt} style={{ color: colors.green, fontSize: '13px' }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '16px' }}>{employees.filter(isInternalEmployee).length}</span>
            <span style={{ color: colors.muted, fontSize: '11px' }}>Active Internal</span>
          </div>
          <div className="ms-auto">
            <Button size="sm" onClick={() => { setShowAddForm(true); setSelected(null); }}
              style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '12px', fontWeight: 600 }}>
              <FontAwesomeIcon icon={faUserPlus} className="me-1" /> Add Employee
            </Button>
          </div>
        </div>
      </div>

      {/* Main Split Panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 110px)' }}>

        {/* Left Panel - Employee List */}
        <div style={{ width: '380px', minWidth: '380px', background: colors.navy2, borderRight: `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column' }}>
          {/* Search */}
          <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '10px', color: colors.muted, fontSize: '12px' }} />
              <input
                type="text" placeholder="Search team..."
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 8px 8px 30px', color: colors.white, fontSize: '13px', outline: 'none' }}
              />
            </div>
          </div>

          {/* Employee List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-5" style={{ color: colors.muted }}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: '32px', marginBottom: '8px' }} /><br />
                No team members found
              </div>
            ) : (
              filteredEmployees.map(emp => (
                <div
                  key={emp.id}
                  onClick={() => { setSelected(emp); setShowAddForm(false); setShowResetPassword(false); }}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                    background: selected?.id === emp.id ? colors.navy3 : 'transparent',
                    borderLeft: selected?.id === emp.id ? `3px solid ${colors.gold}` : '3px solid transparent'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div style={{ color: colors.white, fontWeight: 600, fontSize: '14px' }}>
                        {emp.user_first_name} {emp.user_last_name}
                      </div>
                      <div style={{ color: colors.muted, fontSize: '12px' }}>{emp.user_email}</div>
                    </div>
                    <div className="text-end">
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                        background: isInternalEmployee(emp) ? colors.green + '22' : colors.red + '22',
                        color: isInternalEmployee(emp) ? colors.green : colors.red
                      }}>
                        {isInternalEmployee(emp) ? 'Active' : emp.user_role || 'None'}
                      </span>
                    </div>
                  </div>
                  <div style={{ color: colors.muted, fontSize: '10px', marginTop: '4px' }}>
                    Last login: {formatDate(emp.last_login)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#eef1f6' }}>
          {showAddForm ? (
            /* Add Employee Form */
            <div style={{ padding: '24px' }}>
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '1px', margin: 0 }}>
                  Add <span style={{ color: colors.gold }}>Team Member</span>
                </h3>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0 0' }}>
                  Creates an InternalEmployee account with full platform access.
                </p>
              </div>

              <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e9ecef', maxWidth: '500px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>First Name *</label>
                  <input value={newEmployee.firstName} onChange={e => setNewEmployee({ ...newEmployee, firstName: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Last Name *</label>
                  <input value={newEmployee.lastName} onChange={e => setNewEmployee({ ...newEmployee, lastName: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Email *</label>
                  <input type="email" value={newEmployee.email} onChange={e => setNewEmployee({ ...newEmployee, email: e.target.value })}
                    placeholder="name@kairosiq.ai"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Phone</label>
                  <input value={newEmployee.phone} onChange={e => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }} />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Password *</label>
                  <input type="password" value={newEmployee.password} onChange={e => setNewEmployee({ ...newEmployee, password: e.target.value })}
                    placeholder="Min 8 characters"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }} />
                </div>
                <div className="d-flex gap-2">
                  <Button onClick={handleAddEmployee} disabled={adding}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '14px' }}>
                    {adding ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faUserPlus} className="me-1" /> Create Employee</>}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => setShowAddForm(false)}>Cancel</Button>
                </div>
                <p style={{ color: '#999', fontSize: '11px', marginTop: '12px', marginBottom: 0 }}>
                  Employee will have InternalEmployee role with full access to all platform features (tier 999).
                </p>
              </div>
            </div>
          ) : !selected ? (
            /* Overview when nothing selected */
            <div style={{ padding: '24px' }}>
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '1px', margin: 0 }}>
                  Team <span style={{ color: colors.gold }}>Management</span>
                </h3>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0 0' }}>
                  Select a team member to manage, or click "Add Employee" to create a new account.
                </p>
              </div>

              {/* Team Table */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px' }}>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />All Team Members
                </h6>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee' }}>
                      <th style={{ padding: '8px 4px', color: '#999', fontWeight: 600, textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '8px 4px', color: '#999', fontWeight: 600, textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '8px 4px', color: '#999', fontWeight: 600, textAlign: 'center' }}>Role</th>
                      <th style={{ padding: '8px 4px', color: '#999', fontWeight: 600, textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '8px 4px', color: '#999', fontWeight: 600, textAlign: 'left' }}>Last Login</th>
                      <th style={{ padding: '8px 4px', color: '#999', fontWeight: 600, textAlign: 'left' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id} onClick={() => { setSelected(emp); setShowAddForm(false); }}
                        style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ padding: '8px 4px', fontWeight: 600, color: '#333' }}>{emp.user_first_name} {emp.user_last_name}</td>
                        <td style={{ padding: '8px 4px', color: '#555' }}>{emp.user_email}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: colors.gold }}>{emp.user_role || '—'}</span>
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                          <span style={{
                            fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                            background: isInternalEmployee(emp) ? colors.green + '18' : colors.red + '18',
                            color: isInternalEmployee(emp) ? colors.green : colors.red
                          }}>
                            {isInternalEmployee(emp) ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 4px', color: '#777', fontSize: '11px' }}>{formatDate(emp.last_login)}</td>
                        <td style={{ padding: '8px 4px', color: '#777', fontSize: '11px' }}>{formatDate(emp.created_on)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Employee Detail */
            <div style={{ padding: '24px' }}>
              {/* Profile Header */}
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', letterSpacing: '1px', margin: 0 }}>
                      {selected.user_first_name} {selected.user_last_name}
                    </h3>
                    <div className="d-flex gap-3 mt-2 flex-wrap" style={{ fontSize: '14px' }}>
                      <span style={{ color: '#555' }}><FontAwesomeIcon icon={faEnvelope} className="me-1" style={{ color: colors.gold }} />{selected.user_email}</span>
                      {selected.user_phone && <span style={{ color: '#555' }}><FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: colors.gold }} />{selected.user_phone}</span>}
                    </div>
                  </div>
                  <div className="text-end">
                    <span style={{
                      fontSize: '12px', fontWeight: 700, padding: '4px 12px', borderRadius: '6px',
                      background: isInternalEmployee(selected) ? colors.green + '22' : colors.red + '22',
                      color: isInternalEmployee(selected) ? colors.green : colors.red
                    }}>
                      {isInternalEmployee(selected) ? 'Internal Employee' : selected.user_role || 'No Role'}
                    </span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" onClick={() => setShowResetPassword(!showResetPassword)}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faKey} className="me-1" /> Reset Password
                  </Button>
                  {isInternalEmployee(selected) ? (
                    <Button size="sm" variant="outline-danger" style={{ fontSize: '12px' }} onClick={() => handleDeactivate(selected)} disabled={actionLoading}>
                      <FontAwesomeIcon icon={faPowerOff} className="me-1" /> Deactivate
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline-success" style={{ fontSize: '12px' }} onClick={() => handleReactivate(selected)} disabled={actionLoading}>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Reactivate as Internal
                    </Button>
                  )}
                  <Button size="sm" variant="outline-light" style={{ fontSize: '12px', borderColor: colors.blue, color: colors.blue }}
                    onClick={() => {
                      setEditTeamForm({
                        user_first_name: selected.user_first_name || '',
                        user_last_name: selected.user_last_name || '',
                        user_email: selected.user_email || '',
                        user_phone: selected.user_phone || ''
                      });
                      setEditingTeamMember(true);
                    }}>
                    &#9998; Edit Profile
                  </Button>
                </div>

                {/* Inline Edit Form */}
                {editingTeamMember && (
                  <div style={{ marginTop: '12px', background: '#f0f7ff', borderRadius: '8px', padding: '14px', border: '1px solid #d0e4f7' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: '10px' }}>Edit Profile</div>
                    <div className="d-flex gap-2 flex-wrap mb-2">
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>First Name</label>
                        <input value={editTeamForm.user_first_name} onChange={e => setEditTeamForm({ ...editTeamForm, user_first_name: e.target.value })}
                          style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 8px' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Last Name</label>
                        <input value={editTeamForm.user_last_name} onChange={e => setEditTeamForm({ ...editTeamForm, user_last_name: e.target.value })}
                          style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 8px' }} />
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap mb-2">
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Email</label>
                        <input value={editTeamForm.user_email} onChange={e => setEditTeamForm({ ...editTeamForm, user_email: e.target.value })}
                          style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 8px' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Phone</label>
                        <input value={editTeamForm.user_phone} onChange={e => setEditTeamForm({ ...editTeamForm, user_phone: e.target.value })}
                          placeholder="(480) 555-1234"
                          style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 8px' }} />
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button size="sm" onClick={() => {
                        axiosInstance.put(`/admin/team/${selected.id}`, editTeamForm).then(() => {
                          setAlert({ type: 'success', message: 'Profile updated' });
                          setEditingTeamMember(false);
                          fetchTeam();
                          setSelected({ ...selected, ...editTeamForm } as any);
                        }).catch(() => setAlert({ type: 'danger', message: 'Failed to update' }));
                      }} style={{ background: colors.green, border: 'none', fontSize: '11px', fontWeight: 600 }}>Save</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingTeamMember(false)} style={{ fontSize: '11px' }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Reset Password Form */}
              {showResetPassword && (
                <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '20px', border: `1px solid ${colors.gold}` }}>
                  <h6 style={{ color: colors.gold2, fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Reset Password for {selected.user_first_name}
                  </h6>
                  <div className="d-flex gap-2 align-items-center">
                    <input type="password" placeholder="New password (min 8 characters)" value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      style={{ flex: 1, background: colors.navy3, border: 'none', borderRadius: '6px', padding: '10px', color: colors.white, fontSize: '14px', outline: 'none' }} />
                    <Button size="sm" onClick={handleResetPassword} disabled={actionLoading || newPassword.length < 8}
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                      {actionLoading ? <Spinner size="sm" /> : 'Reset'}
                    </Button>
                    <Button size="sm" variant="outline-light" onClick={() => { setShowResetPassword(false); setNewPassword(''); }}
                      style={{ borderColor: colors.muted, color: colors.muted }}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Account Details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Account Details
                  </h6>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Role</span>
                      <span style={{ fontWeight: 600, color: colors.gold }}>{selected.user_role || '—'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Package</span>
                      <span style={{ fontWeight: 600 }}>{selected.subscription_package_name || '—'}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Created</span>
                      <span>{formatDate(selected.created_on)}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Last Login</span>
                      <span>{formatDate(selected.last_login)}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span style={{ color: '#999' }}>Connected Emails</span>
                      <span style={{ fontWeight: 600 }}>{selected.connected_emails}</span>
                    </div>
                  </div>
                </div>

                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Access Level
                  </h6>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    {isInternalEmployee(selected) ? (
                      <>
                        <div className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: '1px solid #eee' }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />
                          <span>Full platform access (Tier 999)</span>
                        </div>
                        <div className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: '1px solid #eee' }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />
                          <span>Admin dashboard (Subscribers, Contacts, Events)</span>
                        </div>
                        <div className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: '1px solid #eee' }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />
                          <span>AI Operations access</span>
                        </div>
                        <div className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: '1px solid #eee' }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />
                          <span>MLS Portal & Smart CMA</span>
                        </div>
                        <div className="d-flex align-items-center gap-2 py-1">
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />
                          <span>No billing / Lifetime subscription</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-3">
                        <FontAwesomeIcon icon={faPowerOff} style={{ color: colors.red, fontSize: '24px', marginBottom: '8px' }} />
                        <p style={{ color: colors.red, fontSize: '13px', fontWeight: 600, margin: 0 }}>InternalEmployee access revoked</p>
                        <p style={{ color: '#999', fontSize: '12px', margin: '4px 0 0' }}>Current role: {selected.user_role || 'None'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
